// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the wire contract of a shared session (`docs/SHARED_AR_PLAN.md`):
 * what one device tells the others so that everyone sees the same sphere
 * doing the same thing.
 *
 * One device leads and the rest follow. In an open room the lead is the
 * participant who has been there longest; when it leaves, the next
 * longest takes over. In a meeting only a presenter leads, and each
 * connection has a seat (presenter, moderator, audience) that the site
 * assigns from a signed link. Only the lead's state is relayed.
 *
 * Beyond following, a room can do things together (an activity: free
 * exploration, a question on the globe, a choice); those messages and
 * their limits are in `roomActivity.ts` and travel here.
 *
 * Imported by the browser (`roomClient`, `roomSync`), by the room's
 * Durable Object (`workers/rooms`) and by the Pages route in front of it,
 * so it is names, shapes and validation only: no DOM, no Worker globals.
 * Everything that arrives over the socket is checked here, field by
 * field, before either side acts on it — a room is open to anyone with
 * its code.
 */

import {
  NAME_CHARS,
  PIN_NOTE_CHARS,
  cleanText,
  parseActivityStart,
  parseChoices,
  parseHands,
  parseLog,
  parseLogRows,
  parseLookCells,
  parseMine,
  parsePeople,
  parsePins,
  parsePoint,
  parseResults,
  parseRoomActivity,
  parseSpotlight,
  parseTally,
  type ActivityStart,
  type LoggedActivity,
  type LogRow,
  type MyAnswer,
  type RoomHand,
  type RoomPerson,
  type RoomSpotlight,
  type LookCell,
  type RoomActivity,
  type RoomPin,
  type RoomResults,
  type RoomTally,
} from './roomActivity'
import { parseGameClientMessage, parseGameView, type GameClientMessage, type GameView } from './roomGame'
import { PALETTE_IDS, type PaletteId } from './paletteIds'
import { RESTRICTED_STREAM_PACKAGES, STAGE_MAX_MEMBERS } from './xrPlans'

/** What the lead's sphere is showing. */
export interface RoomState {
  /** The dataset on the sphere, or null for the bare Earth. */
  datasetId: string | null
  /** The playhead of a video dataset; null for a picture or no dataset. */
  playback: RoomPlayback | null
  /** The sphere in AR; null while the lead is not in an AR session. */
  globe: RoomGlobe | null
  /** The browser globe's camera; null when the lead has none to report. */
  view: RoomView | null
  /** The layer stack around the dataset; null when the lead reports none. */
  layers: RoomLayers | null
  /**
   * AYNI: what the lead's Khipu shows beside the globe (the Khipu plan,
   * player parity, 2026-10-08). Optional, so a state without it still
   * parses and an older client ignores it; a room Worker built before it
   * drops it, and the room simply sees no captions.
   */
  note?: RoomNote | null
  /**
   * AYNI (multi-globe plan, phase 3, 2026-10-10): the lead's globes when
   * it shows more than one — the layout, which globe is its main one (the
   * clock; `datasetId`, `playback` and `view` above are that globe's), and
   * each globe by position: its dataset, its time rule and who drives it.
   * Optional and absent on one globe, so every room before it still parses.
   */
  layout?: RoomLayout
  primary?: number
  slots?: RoomSlot[]
  /**
   * Whether the globes' cameras are linked (turning one turns them all, as
   * on a single page). False while someone drives a globe: each driven
   * globe's camera then comes in a `slot` message from its driver.
   */
  linked?: boolean
  /**
   * AYNI (owner, 2026-10-22): the colours the lead draws each dataset in —
   * the main globe's, the other globes', the real-time overlays' — so
   * everyone sees the same palette, inversion and colour range. Followers
   * draw them without saving them as their own. Optional, so every room
   * before it still parses; a room Worker built before it drops it.
   */
  colours?: RoomColours[]
  /**
   * AYNI (owner, 2026-10-22): the lead's data pins, and whether its pin
   * graph is on screen. Absent: the lead has none, and neither do followers.
   */
  dataPins?: RoomDataPins
  /**
   * AYNI (satellites plan, 2026-10-22): the lead's Satellites layer, its
   * clock and its selected object. Absent: the lead shows none. A room
   * Worker built before it drops it, and followers see none.
   */
  satellites?: RoomSatellites
}

/** AYNI (satellites plan): the lead's satellites, as satelliteRoom.ts sends and applies them. */
export interface RoomSatellites {
  show: string[]
  colourBy: 'orbit' | 'country' | 'purpose'
  hidden?: string[]
  altitude: 'true' | 'compact'
  trails: boolean
  /** Following real time; otherwise `simMs` (sim time when sent) runs at `speed`, unless held. */
  live: boolean
  simMs?: number
  speed?: number
  playing?: boolean
  /** The selected object's NORAD number. */
  selected?: number
}

/** The show sets and colourings a satellites state may name (satelliteLayer's SHOW_SETS, COLOUR_BYS). */
const ROOM_SAT_SHOW = ['highlights', 'all', 'starlink', 'fengyun-1c-debris', 'cosmos-2251-debris', 'iridium-33-debris']
const ROOM_SAT_COLOUR = ['orbit', 'country', 'purpose']

/** AYNI (satellites plan): the lead's satellites as received, or null when they aren't what they claim. */
export function parseRoomSatellites(raw: unknown): RoomSatellites | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Record<string, unknown>
  const show = Array.isArray(r.show) ? r.show.filter((v): v is string => typeof v === 'string' && ROOM_SAT_SHOW.includes(v)) : []
  if (!show.length || typeof r.colourBy !== 'string' || !ROOM_SAT_COLOUR.includes(r.colourBy)) return null
  const hidden = Array.isArray(r.hidden)
    ? r.hidden.filter((h): h is string => typeof h === 'string' && /^(?:[a-z-]{2,24}|[A-Z]{2,4})$/.test(h)).slice(0, 16)
    : []
  const out: RoomSatellites = {
    show: [...new Set(show)],
    colourBy: r.colourBy as RoomSatellites['colourBy'],
    ...(hidden.length ? { hidden } : {}),
    altitude: r.altitude === 'compact' ? 'compact' : 'true',
    trails: r.trails !== false,
    live: r.live !== false,
  }
  if (!out.live) {
    if (!finite(r.simMs)) return null
    out.simMs = r.simMs
    out.speed = finite(r.speed) && r.speed > 0 && r.speed <= 10_000 ? r.speed : 1
    out.playing = r.playing !== false
  }
  if (Number.isInteger(r.selected) && (r.selected as number) > 0 && (r.selected as number) < 10_000_000) out.selected = r.selected as number
  return out
}

/**
 * AYNI (owner, 2026-10-22): one dataset's colours as the lead draws them,
 * `ColorScaleDisplay` in a compact form: absent fields are the identity
 * (no inversion, the whole range, no limits, no threshold).
 */
export interface RoomColours {
  /** The dataset these colours belong to. */
  id: string
  palette: PaletteId
  inverted?: true
  /** The sub-range the ramp spans, as fractions [lo, hi]; absent: [0, 1]. */
  stretch?: [number, number]
  /** Colour limits as physical values [min, max, axis]. */
  limits?: [number, number, 'linear' | 'log']
  /** Values outside [min, max] hidden; null: no bound on that side. */
  threshold?: [number | null, number | null]
}

/** Most colour entries a state carries: one per globe and one per real-time overlay. */
export const ROOM_MAX_COLOURS = 8

/** AYNI (owner, 2026-10-22): one of the lead's data pins. */
export interface RoomDataPin {
  lat: number
  lon: number
  /** Its colour, as an index into `MEASUREMENT_COLORS`, so a pin is the same colour everywhere. */
  color: number
  /** The name the lead gave it; absent: its own ("Pin 2"). */
  name?: string
  /** A pin "Mark highest & lowest" placed. */
  tag?: 'max' | 'min'
  /** It follows its extreme frame by frame; each device finds the extreme in its own frame. */
  follow?: true
}

/** AYNI (owner, 2026-10-22): the lead's pins and its pin graph. */
export interface RoomDataPins {
  pins: RoomDataPin[]
  /** The lead's pin graph is on its screen (in Tools → Data, or out over the globe). */
  graph?: true
  /** The following pins draw the track they left. */
  track?: true
  /** Where the highest and lowest are looked for; absent: everywhere. */
  surface?: 'land' | 'sea'
}

/** As many pins as the Data tab holds (`MAX_PINS` in `measure/pins.ts`; a test keeps the two equal). */
export const ROOM_MAX_DATA_PINS = 6
/** The longest pin name (`PIN_NAME_MAX` in `measure/pins.ts`). */
export const ROOM_PIN_NAME_CHARS = 32

export type RoomLayout = '1' | '2h' | '2v' | '4'

/** One globe of a lead's layout, by position. */
export interface RoomSlot {
  datasetId: string | null
  /** The globe's time rule in `globeSlots.ts`'s short form ('' or absent: the same date as the main globe). */
  time?: string
  /** The connection driving this globe (its camera, its dataset, its time rule), or null for the presenter. */
  driver?: string | null
  /** The lead's camera on this globe while the cameras are not linked and nobody drives it. */
  view?: RoomView | null
  /** AYNI (multi-globe plan, phase 2): this globe's own layer stack (the main globe's is the state's `layers`). */
  layers?: RoomLayers | null
}

/** Most globes a state may describe. */
export const ROOM_MAX_SLOTS = 4

/**
 * What a driver says of its globe: where its camera is and, when it
 * changed them, its dataset and its time rule. The presenter's page takes
 * these into its own globe and its next state carries them to everyone.
 */
export interface RoomSlotUpdate {
  view: RoomView | null
  datasetId?: string | null
  time?: string
}

/** Who drives a globe, for everyone: its position, their connection id and the name they gave. */
export interface RoomDriver {
  slot: number
  id: string
  name: string | null
}

/** A driver's live microphone: the globe's position and the media revision to pull. */
export interface RoomDriverLive {
  slot: number
  rev: number
}

/**
 * A media publisher as the site names it to the room: the presenter, the
 * driver of a globe, or (AYNI, owner 2026-10-12) `spot`: the participant
 * the presenter handed the main globe to.
 */
export type RoomPublisher = 'presenter' | 'spot' | `slot:${number}` | `stage:${number}`

export function parsePublisher(raw: unknown): RoomPublisher | null {
  if (raw === undefined || raw === null || raw === 'presenter') return 'presenter'
  if (raw === 'spot') return 'spot'
  if (typeof raw !== 'string') return null
  const m = /^slot:([0-3])$/.exec(raw)
  if (m) return `slot:${Number(m[1])}` as RoomPublisher
  // AYNI (stage plan, owner 2026-11-03): a place on the meeting stage.
  const s = /^stage:([1-9])$/.exec(raw)
  return s && Number(s[1]) <= STAGE_MAX_MEMBERS ? (`stage:${Number(s[1])}` as RoomPublisher) : null
}

/** The stage place a publisher names (1..STAGE_MAX_MEMBERS), or null for any other publisher. */
export function stagePlaceOf(publisher: RoomPublisher): number | null {
  return publisher.startsWith('stage:') ? Number(publisher.slice(6)) : null
}

/**
 * AYNI (stage plan, owner 2026-11-03, `docs/AYNI_XR_MEETING_STAGE_PLAN.md`):
 * someone on the meeting stage besides the presenter, for everyone: their
 * place (1..STAGE_MAX_MEMBERS), connection id, the name they gave, and
 * whether their camera is allowed (a pupil's is not, until the teacher
 * allows it).
 */
export interface RoomStageMember {
  place: number
  id: string
  name: string | null
  camera: boolean
}

/** A stage member's live camera and microphone: the place and the media revision to pull. */
export interface RoomStageLive {
  place: number
  rev: number
}

/**
 * This person's place on the stage, as the room tells only them: invited
 * (`joined` false) or on it, and whether their camera is allowed. Null:
 * not invited, or no longer on it.
 */
export interface RoomMyStage {
  place: number
  camera: boolean
  joined: boolean
}

const isStagePlace = (v: unknown): v is number => Number.isInteger(v) && (v as number) >= 1 && (v as number) <= STAGE_MAX_MEMBERS

export function parseStageMembers(raw: unknown): RoomStageMember[] {
  if (!Array.isArray(raw)) return []
  const out: RoomStageMember[] = []
  for (const item of raw as unknown[]) {
    const m = item as Record<string, unknown> | null
    if (typeof m !== 'object' || m === null || !isStagePlace(m.place) || out.some((o) => o.place === m.place)) continue
    if (typeof m.id !== 'string' || m.id.length === 0 || m.id.length > 64) continue
    out.push({ place: m.place, id: m.id, name: cleanText(m.name, NAME_CHARS), camera: m.camera === true })
  }
  return out.sort((a, b) => a.place - b.place)
}

export function parseStageLive(raw: unknown): RoomStageLive[] {
  if (!Array.isArray(raw)) return []
  const out: RoomStageLive[] = []
  for (const item of raw as unknown[]) {
    const d = item as Record<string, unknown> | null
    if (typeof d !== 'object' || d === null || !isStagePlace(d.place) || out.some((o) => o.place === d.place)) continue
    if (typeof d.rev !== 'number' || !Number.isFinite(d.rev) || d.rev < 0) continue
    out.push({ place: d.place, rev: d.rev })
  }
  return out.sort((a, b) => a.place - b.place)
}

export function parseMyStage(raw: unknown): RoomMyStage | null {
  if (typeof raw !== 'object' || raw === null) return null
  const m = raw as Record<string, unknown>
  return isStagePlace(m.place) ? { place: m.place, camera: m.camera === true, joined: m.joined === true } : null
}

export function parseDriversLive(raw: unknown): RoomDriverLive[] {
  if (!Array.isArray(raw)) return []
  const out: RoomDriverLive[] = []
  for (const item of raw as unknown[]) {
    const d = item as Record<string, unknown> | null
    if (typeof d !== 'object' || d === null || !Number.isInteger(d.slot) || (d.slot as number) < 0 || (d.slot as number) >= ROOM_MAX_SLOTS) continue
    if (typeof d.rev !== 'number' || !Number.isFinite(d.rev) || d.rev < 0) continue
    out.push({ slot: d.slot as number, rev: d.rev })
  }
  return out
}

/** A Khipu step as the room sees it: the Khipu's title, the step's, its captions and its pins. */
export interface RoomNote {
  title: string
  /** The step's title, or ''. */
  step: string
  lines: string[]
  pins: { lat: number; lon: number; note: string; color: number }[]
}

/** The note's limits: a state must fit one message (`ROOM_MAX_MESSAGE_CHARS`) with the rest. */
export const NOTE_TITLE_CHARS = 120
export const NOTE_LINE_CHARS = 300
export const NOTE_MAX_LINES = 3
export const NOTE_PIN_CHARS = 60
/** A Khipu step's pins, as many as the Data tab holds (six since 2026-10-09). */
export const NOTE_MAX_PINS = 6

/** Where the browser globe's camera looks, in MapLibre's own terms. */
export interface RoomView {
  lat: number
  /** Degrees east, -180 to 180. */
  lon: number
  zoom: number
  bearing: number
  pitch: number
  /** AYNI (2026-10-19): the globes shown as the flat 2:1 map (flatMap.ts); absent: globes. A client that knows no flat map ignores it. */
  flat?: true
}

/** The basemap under the dataset, the overlays above it, and the real-time overlay stream. */
export interface RoomLayers {
  basemapId: string | null
  overlays: { id: string; tint: 'source' | 'white' | 'black' }[]
  /** The dataset id of the real-time overlay playing over the dataset, or null. */
  rt: string | null
  /**
   * AYNI: a second real-time overlay, above the first (owner, 2026-10-04).
   * Optional, so a state from a client that knows only one still parses,
   * and an older client simply ignores it.
   */
  rt2?: string | null
  /** AYNI: a third and a fourth (a computer plays four), on the same terms. */
  rt3?: string | null
  rt4?: string | null
  /**
   * AYNI (owner, 2026-10-12): how the lead shows them: the dataset's
   * opacity and eye, and each real-time overlay's. Absent (an older lead,
   * or everything opaque and shown): all opaque and shown.
   */
  look?: RoomLayerLook
}

/** AYNI: one layer's opacity (0 … 1; absent: 1) and eye (absent: shown). */
export interface RoomLayerShade {
  opacity?: number
  hidden?: true
}

/** AYNI (owner, 2026-10-12): the lead's opacities and eyes (RoomLayers.look). */
export interface RoomLayerLook {
  /** The dataset's. */
  base?: RoomLayerShade
  /** The real-time overlays', by dataset id; only those not opaque and shown. */
  rt?: Array<RoomLayerShade & { id: string }>
}

/** AYNI: an opacity as sent: two decimals, so a slider's drift does not resend the state. */
export function roomOpacity(opacity: number): number {
  return Math.round(Math.min(1, Math.max(0, opacity)) * 100) / 100
}

/** AYNI: a layer's shade as sent, or null when it is opaque and shown (nothing to send). */
export function roomShade(opacity: number, hidden: boolean): RoomLayerShade | null {
  const o = roomOpacity(opacity)
  if (o >= 1 && !hidden) return null
  return { ...(o < 1 ? { opacity: o } : {}), ...(hidden ? { hidden: true as const } : {}) }
}

/** AYNI: a shade as received; undefined refuses it. */
function parseShade(raw: unknown): RoomLayerShade | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const r = raw as Record<string, unknown>
  if (r.opacity !== undefined && (typeof r.opacity !== 'number' || !Number.isFinite(r.opacity) || r.opacity < 0 || r.opacity > 1)) return undefined
  if (r.hidden !== undefined && r.hidden !== true) return undefined
  return {
    ...(typeof r.opacity === 'number' ? { opacity: roomOpacity(r.opacity) } : {}),
    ...(r.hidden === true ? { hidden: true as const } : {}),
  }
}

/** AYNI: a look as received: null for none, undefined to refuse the layers. */
export function parseRoomLayerLook(raw: unknown): RoomLayerLook | null | undefined {
  if (raw === undefined || raw === null) return null
  if (typeof raw !== 'object') return undefined
  const l = raw as Record<string, unknown>
  const out: RoomLayerLook = {}
  if (l.base !== undefined) {
    const base = parseShade(l.base)
    if (!base) return undefined
    out.base = base
  }
  if (l.rt !== undefined) {
    if (!Array.isArray(l.rt) || l.rt.length > ROOM_MAX_OVERLAYS) return undefined
    const rt: NonNullable<RoomLayerLook['rt']> = []
    for (const item of l.rt as unknown[]) {
      const shade = parseShade(item)
      const id = (item as Record<string, unknown> | null)?.id
      if (!shade || typeof id !== 'string' || id.length === 0 || id.length > 200) return undefined
      rt.push({ id, ...shade })
    }
    out.rt = rt
  }
  return out
}

/** Most overlays a state may name. */
export const ROOM_MAX_OVERLAYS = 8

export interface RoomPlayback {
  paused: boolean
  /** Seconds into the video when this state was sent. */
  time: number
  /** The video's length in seconds on the lead's device. */
  duration: number
  rate: number
}

export interface RoomGlobe {
  /**
   * The sphere's orientation as a quaternion [x, y, z, w]: in the
   * marker's frame when `aligned`, else in the lead's own session space.
   */
  q: [number, number, number, number]
  scale: number
  /** Whether the lead placed the sphere from the shared marker. */
  aligned: boolean
  /**
   * AYNI (2026-10-22): the headset's zoom (vrFocusZoom.ts), absent at 1×:
   * the magnification, the magnified spot on the sphere and the map's
   * rotation under it, both in the sphere's own frame. A client that knows
   * no zoom ignores it.
   */
  zoom?: { m: number; f: [number, number, number]; c: [number, number, number, number] }
}

/** Deepest zoom a room may carry: zoom 16 (vrFocusZoom.MAX_FOCUS_MAGNIFICATION). */
const ROOM_MAX_ZOOM_M = 8192

/**
 * AYNI (2026-10-22): a lead's headset zoom, or null when absent or bad (a
 * bad one is dropped on its own; the sphere still follows).
 */
function parseRoomGlobeZoom(raw: unknown): RoomGlobe['zoom'] | null {
  if (!raw || typeof raw !== 'object') return null
  const z = raw as Record<string, unknown>
  if (!finite(z.m) || z.m <= 1 || z.m > ROOM_MAX_ZOOM_M) return null
  const f = z.f
  const c = z.c
  if (!Array.isArray(f) || f.length !== 3 || !f.every(finite)) return null
  if (!Array.isArray(c) || c.length !== 4 || !c.every(finite)) return null
  const fl = Math.hypot(...(f as number[]))
  const cl = Math.hypot(...(c as number[]))
  if (fl < 0.9 || fl > 1.1 || cl < 0.9 || cl > 1.1) return null
  return {
    m: z.m,
    f: (f as number[]).map((v) => v / fl) as [number, number, number],
    c: (c as number[]).map((v) => v / cl) as [number, number, number, number],
  }
}

/** A connection's seat in a meeting. */
export type RoomSeat = 'presenter' | 'moderator' | 'audience'

/** A seat as named on the wire, or null. */
export function parseRoomSeat(raw: unknown): RoomSeat | null {
  return raw === 'presenter' || raw === 'moderator' || raw === 'audience' ? raw : null
}

/** Browser → room. */
export type RoomClientMessage =
  | { t: 'state'; s: RoomState }
  /** Lead only: start an activity (replacing any) or end it. */
  | { t: 'activity'; a: ActivityStart | null }
  /** Lead only: close a question and show its results; for a point question, the right place. */
  | { t: 'reveal'; id: string; answer: [number, number] | null }
  /** While exploring: where this device is looking. */
  | { t: 'look'; p: [number, number] }
  /** While exploring: a pin, with a note. */
  | { t: 'pin'; p: [number, number]; note: string }
  /** An answer to the open question: a place, an option's index, or (a poll that takes several) the options' indexes. */
  | { t: 'answer'; id: string; p: [number, number] | null; choice: number | null; choices?: number[] }
  /** This person's display name, optional; null to go back to none. */
  | { t: 'name'; name: string | null }
  /** A participant raises or lowers a hand. */
  | { t: 'hand'; up: boolean }
  /** The presenter (AYNI, 2026-10-12: or a moderator) gives the globe to a connection, or takes it back (null); the one holding it may give it back. */
  | { t: 'spotlight'; id: string | null }
  /** AYNI (owner, 2026-10-12): the presenter takes over the globe it handed out, for a moment (true), or hands it back (false). */
  | { t: 'override'; on: boolean }
  /** AYNI (owner, 2026-10-12): the presenter or a moderator turns a driver's microphone off. */
  | { t: 'mute'; id: string }
  /** AYNI (owner, 2026-10-12): the presenter or a moderator removes someone from the meeting, for the rest of it. */
  | { t: 'kick'; id: string }
  /** AYNI (owner, 2026-10-12): the presenter or a moderator asks for an activity's answers, person by person. */
  | { t: 'logAsk'; id: string }
  /** AYNI (owner, 2026-10-12): the presenter ends the meeting for everyone, now. */
  | { t: 'endMeeting' }
  /**
   * AYNI (multi-globe plan, phase 3): the presenter gives globe `slot` to a
   * connection, or takes it back (null); the one driving it may give it back.
   */
  | { t: 'drive'; slot: number; id: string | null }
  /** A driver: where its globe's camera is now. Relayed only from the globe's driver. */
  | { t: 'slot'; n: number; s: RoomSlotUpdate }
  /**
   * AYNI (stage plan, owner 2026-11-03): the presenter or a moderator
   * invites someone to the stage (whether their camera is allowed), takes
   * them off it, or allows or stops their camera. A moderator manages
   * participants only. Refused in a meeting without the stage.
   */
  | { t: 'stageInvite'; id: string; camera: boolean }
  | { t: 'stageRemove'; id: string }
  | { t: 'stageCamera'; id: string; camera: boolean }
  /** AYNI (stage plan): the invited person accepts; `stageLeave` declines an invitation or leaves the stage. */
  | { t: 'stageJoin' }
  | { t: 'stageLeave' }
  /** AYNI (meeting games plan): a game's messages (`roomGame.ts`). */
  | GameClientMessage

/**
 * AYNI: what a meeting lends its people (the Education tier, owner
 * 2026-10-05): `data` is the data tools — values, pins and the graph, Line
 * and Box — at the Education level, on the host's plan, while connected to
 * the meeting. Nothing about a person is stored.
 * AYNI (multi-globe plan, owner 2026-10-10): `globes` is how many globes
 * side by side the meeting lends (an Education plan's meeting lends four).
 */
export interface RoomGrants {
  data: boolean
  globes?: number
  /** AYNI (meeting games plan, decision D3): the meeting may play games (an Education host). */
  games?: boolean
  /**
   * AYNI (stage plan, owner 2026-11-03): the meeting has the stage — the
   * presenter and up to STAGE_MAX_MEMBERS more on camera and microphone.
   * `class` (an Education host): a pupil invited joins with the microphone
   * only unless the teacher allows their camera; `open` otherwise.
   */
  stage?: 'open' | 'class'
  /**
   * AYNI (owner, 2026-10-13): the host's stream packages (`'*'`: every
   * package), lent to a participant while the sphere is in their hands
   * (exploring, holding or driving a globe).
   */
  packages?: string[]
}

/** A stream package as a meeting may lend it: an id, or `'*'` for every package. */
const LENT_PACKAGE = /^(\*|[a-z][a-z0-9-]{1,39})$/
/** Most packages a meeting lends. */
export const MAX_LENT_PACKAGES = 16

/**
 * The packages a meeting lends, from `X-Meeting-Packages` or a welcome; empty
 * when none. `'*'` stands for every package but a restricted one
 * (`RESTRICTED_STREAM_PACKAGES`: an Education host's `xr-education`), which is
 * kept beside it.
 */
export function parseLentPackages(raw: unknown): string[] {
  const list = typeof raw === 'string' ? raw.split(',') : Array.isArray(raw) ? raw : []
  const out: string[] = []
  for (const item of list as unknown[]) {
    const p = typeof item === 'string' ? item.trim() : ''
    if (LENT_PACKAGE.test(p) && !out.includes(p)) out.push(p)
  }
  return out.includes('*') ? ['*', ...RESTRICTED_STREAM_PACKAGES.filter((p) => out.includes(p))] : out.slice(0, MAX_LENT_PACKAGES)
}

/** Room → browser. */
export type RoomServerMessage =
  | {
      t: 'welcome'
      /** This connection's id. */
      you: string
      /** The lead's id. */
      lead: string | null
      /** People in the room, this one included. */
      count: number
      /** The lead's latest state, for someone arriving mid-session. */
      state: RoomState | null
      /** Whether the room is a meeting. */
      meeting: boolean
      /** This connection's seat in a meeting; null in an open room. */
      seat: RoomSeat | null
      /** The presenter's live camera and microphone, as a revision; null when not live. */
      media: number | null
      /**
       * This connection's media ticket in a meeting, null in an open room:
       * pulling the presenter's camera asks for it, so only someone in the
       * room can, and one connection holds one stream at a time.
       */
      ticket: string | null
      /** The activity under way, for someone arriving in the middle of it. */
      activity: RoomActivity | null
      /** A revealed question's results, while it is still up. */
      results: RoomResults | null
      /** Who has the globe, when the presenter has handed it over. */
      spotlight: RoomSpotlight | null
      /**
       * AYNI (owner, 2026-10-05): what the meeting's host lends everyone in
       * it for the meeting's life, set when it was created from the host's
       * plan; null in an open room or when nothing is lent.
       */
      grants: RoomGrants | null
      /** AYNI (multi-globe plan): who drives which globe, and each driven globe's last camera. */
      drivers: RoomDriver[]
      driven: { n: number; s: RoomSlotUpdate }[]
      /** AYNI (multi-globe plan): the drivers' live microphones, each as a revision (pull again on a new one). */
      driversLive: RoomDriverLive[]
      /** AYNI (owner, 2026-10-12): the main globe's driver's live microphone, as a revision; null when off. */
      spotLive: number | null
      /** AYNI (owner, 2026-10-12): this person in the meeting — their number and the name the room has for them; null in an open room. */
      me: { n: number; name: string | null } | null
      /** AYNI (stage plan): who is on the stage besides the presenter, their live media, and this person's own place (invited or on it). */
      stage: RoomStageMember[]
      stageLive: RoomStageLive[]
      myStage: RoomMyStage | null
    }
  | { t: 'roster'; lead: string | null; count: number; meeting: boolean; spotlight: RoomSpotlight | null; drivers: RoomDriver[]; stage: RoomStageMember[] }
  /**
   * AYNI (stage plan): to one person, their place on the stage changed —
   * invited, joined, camera allowed or stopped, or null when it ended;
   * `why` says what happened (`removed` by the presenter or a moderator,
   * `muted`: their microphone was turned off, which their page applies).
   */
  | { t: 'stage'; me: RoomMyStage | null; why?: 'removed' | 'muted' }
  | { t: 'state'; s: RoomState }
  /** AYNI (multi-globe plan): a driver moved its globe. */
  | { t: 'slot'; n: number; s: RoomSlotUpdate }
  /**
   * The presenter went live (a new revision: pull again) or stopped (null).
   * Only the revision crosses the socket; what to pull is asked of the
   * site, which keeps the Realtime session ids server-side.
   */
  | { t: 'media'; rev: number | null; drivers?: RoomDriverLive[]; spot?: number | null; stage?: RoomStageLive[] }
  /** An activity started, changed (closed) or ended. */
  | { t: 'activity'; a: RoomActivity | null }
  /** To the lead and the moderator: where people are looking, binned. */
  | { t: 'looks'; cells: LookCell[] }
  /** To the lead and the moderator: the pins so far. */
  | { t: 'pins'; pins: RoomPin[] }
  /** To the lead and the moderator: how the open question is going. */
  | { t: 'tally'; tally: RoomTally }
  /** To everyone: a revealed question's results; null when they are taken down. */
  | { t: 'results'; r: RoomResults | null }
  /** To the presenter and the moderator: the raised hands, oldest first. */
  | { t: 'hands'; hands: RoomHand[] }
  /** AYNI (owner, 2026-10-12): to the presenter and moderators, who is in the meeting. */
  | { t: 'people'; people: RoomPerson[] }
  /** AYNI: to the presenter and moderators, the meeting's activities so far. */
  | { t: 'log'; acts: LoggedActivity[] }
  /** AYNI: to whoever asked (`logAsk`), one activity's answers by person; empty for a poll. */
  | { t: 'logRows'; id: string; rows: LogRow[] }
  /** AYNI: to each participant, their own answers this meeting. */
  | { t: 'mine'; mine: MyAnswer[] }
  /** AYNI: to someone just removed from the meeting, before the room closes their connection. */
  | { t: 'kicked' }
  /** AYNI: to everyone, the presenter ended the meeting (and to anyone arriving after, until its booked end). */
  | { t: 'ended' }
  /** AYNI (meeting games plan): the game as this connection may see it; null when there is none. */
  | { t: 'game'; v: GameView | null }

/** Largest message a browser may send the room, in characters. AYNI (multi-globe plan): 4096, from 2048, for a state with four globes. */
export const ROOM_MAX_MESSAGE_CHARS = 4096
/**
 * Largest message the room sends: the aggregates (heat map, pins, results)
 * are bigger than a state. AYNI (2026-10-12): 65536, from 32768, for an
 * activity's answers by person (fifty people's pins and notes). AYNI
 * (2026-10-17): 524288, for meetings of 500: the people list is about 80 KB
 * at 500, and an activity's answers by person are cut to fit (rooms Worker,
 * `logRows`); Cloudflare's own limit on a WebSocket message is 1 MiB.
 */
export const ROOM_MAX_SERVER_MESSAGE_CHARS = 524288
/** Most people in one open room. */
export const ROOM_MAX_PEOPLE = 40
/**
 * AYNI: how long an open room keeps what it stored once nobody is in it. Here,
 * not in the rooms Worker's entry module: the Workers runtime refuses an entry
 * module that exports anything but handlers and Durable Object classes.
 */
export const OPEN_ROOM_IDLE_MS = 24 * 60 * 60 * 1000
/**
 * Most people in one meeting, whatever the plan says. AYNI (owner,
 * 2026-10-06): 50 until a load test showed more; 500 since the load test of
 * 2026-10-17 (scripts/loadtest on branch test/meeting-load): 500 people in
 * one room in sync with the roster gathered (PR #109), and one presenter's
 * camera to 500 viewers through Realtime, pulls spread by `pullSpread`.
 */
export const MEETING_MAX_PEOPLE = 500
/**
 * AYNI: set by the site's room route, never taken from the caller. "1" when
 * the connection belongs to a signed-in account; a guest (a room link, no
 * account) never leads an open room and only watches.
 */
export const ROOM_MEMBER_HEADER = 'X-Room-Member'
/** AYNI: set by the site's room route: how many people an open room its first person opens may hold (their plan's rooms). */
export const ROOM_CAPACITY_HEADER = 'X-Room-Capacity'
/**
 * AYNI (owner, 2026-10-12): set by the site's room route: this person's key
 * in a meeting (`isPersonKey`), so a reconnection is the same person and a
 * removed one stays out. Stripped from what a browser sends, like every
 * `X-Room-*` header. AYNI (security fix PR 4, S-10, 2026-10-21): the door
 * derives it itself from a cookie it mints (`functions/api/room/_room-lib.ts`);
 * a `?pk=` the browser sends is ignored there, and read only by the rooms
 * Worker's own development handler (`ROOM_DEV=1`).
 */
export const ROOM_PERSON_HEADER = 'X-Room-Person'

/**
 * AYNI (security fix, 2026-10-07): the room's internal routes (`/init`,
 * `/status`, `/media`, `/media/pulled`…) answer only a request that carries
 * this header with the shared value (`ROOM_INTERNAL_SECRET` on the site and
 * on the rooms Worker; the development value when neither sets one). The
 * site's door strips it, with every other `X-Room-*` and `X-Meeting-*`
 * header, from what a browser sends, so only the site's own routes can set
 * it. Defence in depth: the room has no public address, and the door now
 * hands sockets to `/ws` only.
 */
export const ROOM_INTERNAL_HEADER = 'X-Room-Internal'
/** AYNI (security fix PR 4, S-11): only local development ever uses this value; see `roomInternalSecret`. */
export const DEV_ROOM_INTERNAL_SECRET = 'ayni-room-internal-dev'

/**
 * What `roomInternalSecret` reads: the secret, and what tells a deployment
 * from a developer's machine. On the site, Cloudflare Pages sets `CF_PAGES`
 * and `CF_PAGES_BRANCH` at runtime (`functions/api/ingest.ts`
 * `environmentOf` reads the same two); the rooms Worker has neither and
 * says so itself. `ROOM_DEV=1` (`.dev.vars`) marks local development on
 * either side.
 */
export interface RoomSecretEnv {
  ROOM_INTERNAL_SECRET?: string
  ROOM_DEV?: string
  CF_PAGES?: string
  CF_PAGES_BRANCH?: string
}

/** Whether this is a production deployment of the site: Pages, on the branch `production` (or `main`, as TerraViz deployed it). */
export function isProductionSite(env: RoomSecretEnv): boolean {
  return !!env.CF_PAGES && (env.CF_PAGES_BRANCH === 'production' || env.CF_PAGES_BRANCH === 'main')
}

/**
 * The value the internal header carries for this deployment.
 *
 * AYNI (security fix PR 4, S-11, 2026-10-21): `ROOM_INTERNAL_SECRET` when it
 * is set. Unset, the development value stands in only in local development
 * (`ROOM_DEV=1`, or a site that is not a production deployment: `wrangler
 * pages dev`, tests, a preview); in production it **throws**, so a
 * deployment without the secret refuses its internal routes instead of
 * answering to a value anyone can read in this file. The rooms Worker, which
 * has no `CF_PAGES`, passes `production = true` itself: there, only
 * `ROOM_DEV=1` opens the development value. Both sides must carry the same
 * value, set before either deploys.
 */
export function roomInternalSecret(env: RoomSecretEnv, production: boolean = isProductionSite(env)): string {
  const set = (env.ROOM_INTERNAL_SECRET ?? '').trim()
  if (set) return set
  if (env.ROOM_DEV === '1' || !production) return DEV_ROOM_INTERNAL_SECRET
  throw new Error('ROOM_INTERNAL_SECRET is not set: set the same value on the site and on the rooms Worker (ROOM_DEV=1 only for local development)')
}

/** Headers for a request from the site to a room's internal route. */
export function roomInternalHeaders(env: RoomSecretEnv, extra: Record<string, string> = {}): Record<string, string> {
  return { ...extra, [ROOM_INTERNAL_HEADER]: roomInternalSecret(env) }
}

/** The one address the room takes sockets on: the door forwards every connection here, with GET. */
export const ROOM_SOCKET_URL = 'https://room/ws'

/**
 * A browser's headers made safe to hand to a room: every `X-Room-*` and
 * `X-Meeting-*` header removed (the door sets its own after), so nothing a
 * caller sends can declare a meeting, choose a seat, a host or a grant, or
 * pass for the site.
 */
export function stripRoomHeaders(from: Pick<Headers, 'forEach'>): Headers {
  const out = new Headers()
  from.forEach((value, name) => {
    const n = name.toLowerCase()
    if (n.startsWith('x-room-') || n.startsWith('x-meeting-')) return
    out.set(name, value)
  })
  return out
}

/** Room codes: letters and digits that are hard to misread aloud or on paper. */
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
const CODE_PATTERN = /^[A-Z0-9]{4,12}$/

/** A code as typed or scanned, made canonical; null when it is not one. */
export function normalizeRoomCode(raw: string | null | undefined): string | null {
  const code = (raw ?? '').trim().toUpperCase()
  return CODE_PATTERN.test(code) ? code : null
}

/** A fresh six-character code. `random` returns values in [0, 1). */
export function newRoomCode(random: () => number = Math.random): string {
  let code = ''
  for (let i = 0; i < 6; i++) code += CODE_ALPHABET[Math.floor(random() * CODE_ALPHABET.length)]
  return code
}

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

/** A state as received, or null when any part of it is not what it claims. */
export function parseRoomState(raw: unknown): RoomState | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Record<string, unknown>
  const datasetId = r.datasetId
  if (datasetId !== null && (typeof datasetId !== 'string' || datasetId.length === 0 || datasetId.length > 200)) {
    return null
  }
  let playback: RoomPlayback | null = null
  if (r.playback !== null && r.playback !== undefined) {
    const p = r.playback as Record<string, unknown>
    if (typeof r.playback !== 'object' || typeof p.paused !== 'boolean') return null
    if (!finite(p.time) || !finite(p.duration) || !finite(p.rate)) return null
    if (p.time < 0 || p.duration <= 0 || p.rate <= 0 || p.rate > 16) return null
    playback = { paused: p.paused, time: p.time, duration: p.duration, rate: p.rate }
  }
  let globe: RoomGlobe | null = null
  if (r.globe !== null && r.globe !== undefined) {
    const g = r.globe as Record<string, unknown>
    if (typeof r.globe !== 'object' || typeof g.aligned !== 'boolean') return null
    if (!finite(g.scale) || g.scale <= 0 || g.scale > 100) return null
    const q = g.q
    if (!Array.isArray(q) || q.length !== 4 || !q.every(finite)) return null
    const [x, y, z, w] = q as number[]
    const norm = Math.hypot(x, y, z, w)
    // A rotation is a unit quaternion; anything far from one is not a pose.
    if (norm < 0.9 || norm > 1.1) return null
    globe = { q: [x / norm, y / norm, z / norm, w / norm], scale: g.scale, aligned: g.aligned }
    const zoom = parseRoomGlobeZoom(g.zoom)
    if (zoom) globe.zoom = zoom
  }
  let view: RoomView | null = null
  if (r.view !== null && r.view !== undefined) {
    // AYNI (2026-10-19): the one view check (parseRoomView), so the flat map is kept here too.
    view = parseRoomView(r.view)
    if (!view) return null
  }
  const layers = parseRoomLayers(r.layers)
  if (layers === undefined) return null
  const note = r.note === null || r.note === undefined ? null : parseRoomNote(r.note)
  // AYNI (multi-globe plan): the lead's globes, when it says any; a bad
  // description of them is dropped on its own, as a note is.
  const globes = parseRoomGlobes(r)
  // AYNI (owner, 2026-10-22): the lead's colours and data pins; bad entries are dropped on their own, as a note is.
  const colours = parseRoomColours(r.colours)
  const dataPins = parseRoomDataPins(r.dataPins)
  // AYNI (satellites plan): the lead's satellites; a bad one is dropped on its own.
  const satellites = parseRoomSatellites(r.satellites)
  return {
    datasetId, playback, globe, view, layers, ...(note ? { note } : {}), ...globes,
    ...(colours.length ? { colours } : {}),
    ...(dataPins ? { dataPins } : {}),
    ...(satellites ? { satellites } : {}),
  }
}

const isFraction = (v: unknown): v is number => finite(v) && v >= 0 && v <= 1

/** AYNI: one dataset's colours as received, or null when any part is not what it claims. */
export function parseRoomColour(raw: unknown): RoomColours | null {
  if (typeof raw !== 'object' || raw === null) return null
  const c = raw as Record<string, unknown>
  if (typeof c.id !== 'string' || c.id.length === 0 || c.id.length > 200) return null
  if (!(PALETTE_IDS as readonly unknown[]).includes(c.palette)) return null
  const out: RoomColours = { id: c.id, palette: c.palette as PaletteId }
  if (c.inverted !== undefined) {
    if (c.inverted !== true) return null
    out.inverted = true
  }
  if (c.stretch !== undefined) {
    const s = c.stretch
    if (!Array.isArray(s) || s.length !== 2 || !isFraction(s[0]) || !isFraction(s[1]) || s[0] >= s[1]) return null
    out.stretch = [s[0], s[1]]
  }
  if (c.limits !== undefined) {
    const l = c.limits
    if (!Array.isArray(l) || l.length !== 3 || !finite(l[0]) || !finite(l[1]) || l[0] >= l[1]) return null
    if (l[2] !== 'linear' && l[2] !== 'log') return null
    if (l[2] === 'log' && l[0] <= 0) return null
    out.limits = [l[0], l[1], l[2]]
  }
  if (c.threshold !== undefined) {
    const t = c.threshold
    const bound = (v: unknown): v is number | null => v === null || finite(v)
    if (!Array.isArray(t) || t.length !== 2 || !bound(t[0]) || !bound(t[1])) return null
    if (t[0] !== null && t[1] !== null && t[0] > t[1]) return null
    out.threshold = [t[0], t[1]]
  }
  return out
}

/** AYNI: the lead's colours as received: the good entries, one per dataset, at most `ROOM_MAX_COLOURS`. */
export function parseRoomColours(raw: unknown): RoomColours[] {
  if (!Array.isArray(raw)) return []
  const out: RoomColours[] = []
  for (const item of raw.slice(0, ROOM_MAX_COLOURS) as unknown[]) {
    const c = parseRoomColour(item)
    if (c && !out.some((o) => o.id === c.id)) out.push(c)
  }
  return out
}

/**
 * AYNI: a data pin's place, longitude wrapped, rounded to about ten metres
 * (finer than an activity's `parsePoint`: a pin on a city's close-up data
 * reads one building's value); null when it is not on the Earth.
 */
export function dataPinPoint(lat: unknown, lon: unknown): [number, number] | null {
  if (!finite(lat) || !finite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 540) return null
  const wrapped = ((((lon + 180) % 360) + 360) % 360) - 180
  return [Math.round(lat * 1e4) / 1e4, Math.round(wrapped * 1e4) / 1e4]
}

/**
 * AYNI: the lead's data pins as received, or null for none. A malformed pin
 * is dropped on its own, a second pin of the same colour too (a colour is a
 * pin's identity on the graph).
 */
export function parseRoomDataPins(raw: unknown): RoomDataPins | null {
  if (typeof raw !== 'object' || raw === null) return null
  const d = raw as Record<string, unknown>
  const pins: RoomDataPin[] = []
  if (Array.isArray(d.pins)) {
    for (const item of d.pins.slice(0, ROOM_MAX_DATA_PINS) as unknown[]) {
      const p = item as Record<string, unknown> | null
      if (typeof p !== 'object' || p === null) continue
      const at = dataPinPoint(p.lat, p.lon)
      if (!at) continue
      if (!Number.isInteger(p.color) || (p.color as number) < 0 || (p.color as number) >= ROOM_MAX_DATA_PINS) continue
      if (pins.some((q) => q.color === p.color)) continue
      const pin: RoomDataPin = { lat: at[0], lon: at[1], color: p.color as number }
      const name = cleanText(p.name, ROOM_PIN_NAME_CHARS)
      if (name) pin.name = name
      if (p.tag === 'max' || p.tag === 'min') {
        pin.tag = p.tag
        if (p.follow === true) pin.follow = true
      }
      pins.push(pin)
    }
  }
  const graph = d.graph === true
  if (!pins.length && !graph) return null
  return {
    pins,
    ...(graph ? { graph: true as const } : {}),
    ...(d.track === true ? { track: true as const } : {}),
    ...(d.surface === 'land' || d.surface === 'sea' ? { surface: d.surface } : {}),
  }
}

/**
 * A layer stack as received; null for "none", undefined when it is not one
 * (the caller refuses the state, or drops a globe's stack).
 */
export function parseRoomLayers(raw: unknown): RoomLayers | null | undefined {
  if (raw === null || raw === undefined) return null
  if (typeof raw !== 'object') return undefined
  const l = raw as Record<string, unknown>
  const id = (value: unknown): value is string => typeof value === 'string' && value.length > 0 && value.length <= 200
  if (l.basemapId !== null && !id(l.basemapId)) return undefined
  if (l.rt !== null && !id(l.rt)) return undefined
  for (const extra of [l.rt2, l.rt3, l.rt4]) if (extra !== undefined && extra !== null && !id(extra)) return undefined
  if (!Array.isArray(l.overlays) || l.overlays.length > ROOM_MAX_OVERLAYS) return undefined
  const overlays: RoomLayers['overlays'] = []
  for (const raw of l.overlays as unknown[]) {
    const o = raw as Record<string, unknown> | null
    if (typeof o !== 'object' || o === null || !id(o.id)) return undefined
    if (o.tint !== 'source' && o.tint !== 'white' && o.tint !== 'black') return undefined
    overlays.push({ id: o.id, tint: o.tint })
  }
  const look = parseRoomLayerLook(l.look)
  if (look === undefined) return undefined
  return {
    basemapId: l.basemapId,
    overlays,
    rt: l.rt,
    ...(typeof l.rt2 === 'string' ? { rt2: l.rt2 } : {}),
    ...(typeof l.rt3 === 'string' ? { rt3: l.rt3 } : {}),
    ...(typeof l.rt4 === 'string' ? { rt4: l.rt4 } : {}),
    ...(look && (look.base || look.rt?.length) ? { look } : {}),
  }
}

/** A camera as received, or null. */
export function parseRoomView(raw: unknown): RoomView | null {
  if (typeof raw !== 'object' || raw === null) return null
  const v = raw as Record<string, unknown>
  if (!finite(v.lat) || !finite(v.lon) || !finite(v.zoom) || !finite(v.bearing) || !finite(v.pitch)) return null
  if (Math.abs(v.lat) > 90 || v.zoom < -4 || v.zoom > 30 || v.pitch < 0 || v.pitch > 90) return null
  const wrap = (deg: number): number => ((((deg + 180) % 360) + 360) % 360) - 180
  return { lat: v.lat, lon: wrap(v.lon), zoom: v.zoom, bearing: wrap(v.bearing), pitch: v.pitch, ...(v.flat === true ? { flat: true as const } : {}) }
}

export function parseRoomLayout(raw: unknown): RoomLayout | null {
  return raw === '1' || raw === '2h' || raw === '2v' || raw === '4' ? raw : null
}

const TIME_RULE = /^([+-]?\d{1,5}[dmy]|=\d{4}(-\d{2}(-\d{2})?)?)$/

/** The globes a state names (`layout`, `primary`, `slots`, `linked`), or nothing when it names none or names them badly. */
function parseRoomGlobes(r: Record<string, unknown>): Pick<RoomState, 'layout' | 'primary' | 'slots' | 'linked'> {
  const layout = parseRoomLayout(r.layout)
  if (!layout || !Array.isArray(r.slots) || r.slots.length > ROOM_MAX_SLOTS) return {}
  const slots: RoomSlot[] = []
  for (const raw of r.slots as unknown[]) {
    const s = raw as Record<string, unknown> | null
    if (typeof s !== 'object' || s === null) return {}
    const datasetId = s.datasetId
    if (datasetId !== null && (typeof datasetId !== 'string' || datasetId.length === 0 || datasetId.length > 200)) return {}
    const slot: RoomSlot = { datasetId: datasetId as string | null }
    if (typeof s.time === 'string' && s.time) {
      if (!TIME_RULE.test(s.time)) return {}
      slot.time = s.time
    }
    if (typeof s.driver === 'string' && s.driver.length > 0 && s.driver.length <= 64) slot.driver = s.driver
    else if (s.driver === null) slot.driver = null
    if (s.view !== undefined && s.view !== null) {
      const view = parseRoomView(s.view)
      if (!view) return {}
      slot.view = view
    }
    if (s.layers !== undefined) {
      const layers = parseRoomLayers(s.layers)
      if (layers === undefined) return {}
      slot.layers = layers
    }
    slots.push(slot)
  }
  const primary = Number.isInteger(r.primary) && (r.primary as number) >= 0 && (r.primary as number) < ROOM_MAX_SLOTS ? (r.primary as number) : 0
  return { layout, primary, slots, linked: r.linked !== false }
}

/** A driver's slot message, or null. */
export function parseSlotUpdate(raw: unknown): RoomSlotUpdate | null {
  if (typeof raw !== 'object' || raw === null) return null
  const s = raw as Record<string, unknown>
  const out: RoomSlotUpdate = { view: null }
  if (s.view !== null && s.view !== undefined) {
    const view = parseRoomView(s.view)
    if (!view) return null
    out.view = view
  }
  if (s.datasetId === null) out.datasetId = null
  else if (typeof s.datasetId === 'string') {
    if (!s.datasetId || s.datasetId.length > 200) return null
    out.datasetId = s.datasetId
  }
  if (typeof s.time === 'string') {
    if (s.time && !TIME_RULE.test(s.time)) return null
    out.time = s.time
  }
  return out
}

const isSlotIndex = (v: unknown): v is number => Number.isInteger(v) && (v as number) >= 0 && (v as number) < ROOM_MAX_SLOTS

/** The drivers a roster or welcome lists; an odd list is read as none. */
export function parseDrivers(raw: unknown): RoomDriver[] {
  if (!Array.isArray(raw)) return []
  const out: RoomDriver[] = []
  for (const item of raw as unknown[]) {
    const d = item as Record<string, unknown> | null
    if (typeof d !== 'object' || d === null || !isSlotIndex(d.slot) || typeof d.id !== 'string' || !d.id || d.id.length > 64) continue
    out.push({ slot: d.slot, id: d.id, name: cleanText(d.name, NAME_CHARS) })
  }
  return out
}

/**
 * A note as received, cut to its limits, or null. A malformed note is
 * dropped on its own rather than refusing the state: the globe matters
 * more than its captions.
 */
export function parseRoomNote(raw: unknown): RoomNote | null {
  if (typeof raw !== 'object' || raw === null) return null
  const n = raw as Record<string, unknown>
  const lines = Array.isArray(n.lines)
    ? n.lines.map((l) => cleanText(l, NOTE_LINE_CHARS)).filter((l): l is string => !!l).slice(0, NOTE_MAX_LINES)
    : []
  const pins: RoomNote['pins'] = []
  if (Array.isArray(n.pins)) {
    for (const raw of n.pins.slice(0, NOTE_MAX_PINS) as unknown[]) {
      const p = raw as Record<string, unknown> | null
      if (typeof p !== 'object' || p === null) continue
      const at = parsePoint([p.lat, p.lon])
      if (!at) continue
      const color = Number.isInteger(p.color) && (p.color as number) >= 0 && (p.color as number) < NOTE_MAX_PINS ? (p.color as number) : 0
      pins.push({ lat: at[0], lon: at[1], note: cleanText(p.note, NOTE_PIN_CHARS) ?? '', color })
    }
  }
  const title = cleanText(n.title, NOTE_TITLE_CHARS) ?? ''
  const step = cleanText(n.step, NOTE_TITLE_CHARS) ?? ''
  if (!title && !step && !lines.length && !pins.length) return null
  return { title, step, lines, pins }
}

function parseJson(text: unknown, max = ROOM_MAX_MESSAGE_CHARS): Record<string, unknown> | null {
  if (typeof text !== 'string' || text.length > max) return null
  try {
    const value: unknown = JSON.parse(text)
    return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null
  } catch {
    return null
  }
}

/** What a browser sent, or null. */
export function parseClientMessage(text: unknown): RoomClientMessage | null {
  const msg = parseJson(text)
  if (!msg) return null
  switch (msg.t) {
    case 'state': {
      const s = parseRoomState(msg.s)
      return s ? { t: 'state', s } : null
    }
    case 'activity': {
      if (msg.a === null) return { t: 'activity', a: null }
      const a = parseActivityStart(msg.a)
      return a ? { t: 'activity', a } : null
    }
    case 'reveal': {
      if (!isActivityId(msg.id)) return null
      const answer = msg.answer === null || msg.answer === undefined ? null : parsePoint(msg.answer)
      if (msg.answer !== null && msg.answer !== undefined && !answer) return null
      return { t: 'reveal', id: msg.id, answer }
    }
    case 'look': {
      const p = parsePoint(msg.p)
      return p ? { t: 'look', p } : null
    }
    case 'pin': {
      const p = parsePoint(msg.p)
      return p ? { t: 'pin', p, note: cleanText(msg.note, PIN_NOTE_CHARS) ?? '' } : null
    }
    case 'answer': {
      if (!isActivityId(msg.id)) return null
      const p = msg.p === null || msg.p === undefined ? null : parsePoint(msg.p)
      const choice = Number.isInteger(msg.choice) && (msg.choice as number) >= 0 && (msg.choice as number) < 6 ? (msg.choice as number) : null
      const choices = msg.choices === undefined || msg.choices === null ? null : parseChoices(msg.choices)
      if (msg.choices !== undefined && msg.choices !== null && !choices) return null
      // Exactly one kind of answer.
      if ([p, choice, choices].filter((x) => x !== null).length !== 1) return null
      return choices ? { t: 'answer', id: msg.id, p: null, choice: null, choices } : { t: 'answer', id: msg.id, p, choice }
    }
    case 'name':
      return { t: 'name', name: msg.name === null ? null : cleanText(msg.name, NAME_CHARS) }
    case 'hand':
      return typeof msg.up === 'boolean' ? { t: 'hand', up: msg.up } : null
    case 'spotlight':
      if (msg.id === null) return { t: 'spotlight', id: null }
      return typeof msg.id === 'string' && msg.id.length > 0 && msg.id.length <= 64 ? { t: 'spotlight', id: msg.id } : null
    case 'override':
      return typeof msg.on === 'boolean' ? { t: 'override', on: msg.on } : null
    case 'mute':
    case 'kick':
      return typeof msg.id === 'string' && msg.id.length > 0 && msg.id.length <= 64 ? { t: msg.t, id: msg.id } : null
    case 'logAsk':
      return isActivityId(msg.id) ? { t: 'logAsk', id: msg.id } : null
    case 'endMeeting':
      return { t: 'endMeeting' }
    case 'drive': {
      if (!isSlotIndex(msg.slot)) return null
      if (msg.id === null) return { t: 'drive', slot: msg.slot, id: null }
      return typeof msg.id === 'string' && msg.id.length > 0 && msg.id.length <= 64 ? { t: 'drive', slot: msg.slot, id: msg.id } : null
    }
    case 'stageInvite':
    case 'stageCamera':
      return typeof msg.id === 'string' && msg.id.length > 0 && msg.id.length <= 64 && typeof msg.camera === 'boolean'
        ? { t: msg.t, id: msg.id, camera: msg.camera }
        : null
    case 'stageRemove':
      return typeof msg.id === 'string' && msg.id.length > 0 && msg.id.length <= 64 ? { t: 'stageRemove', id: msg.id } : null
    case 'stageJoin':
    case 'stageLeave':
      return { t: msg.t }
    case 'slot': {
      if (!isSlotIndex(msg.n)) return null
      const s = parseSlotUpdate(msg.s)
      return s ? { t: 'slot', n: msg.n, s } : null
    }
    default:
      return parseGameClientMessage(msg)
  }
}

const isActivityId = (v: unknown): v is string => typeof v === 'string' && /^[\w-]{1,40}$/.test(v)

const idOrNull = (v: unknown): v is string | null =>
  v === null || (typeof v === 'string' && v.length > 0 && v.length <= 64)

/** What the room sent, or null. */
export function parseServerMessage(text: unknown): RoomServerMessage | null {
  const msg = parseJson(text, ROOM_MAX_SERVER_MESSAGE_CHARS)
  if (!msg) return null
  switch (msg.t) {
    case 'activity': {
      if (msg.a === null) return { t: 'activity', a: null }
      const a = parseRoomActivity(msg.a)
      return a ? { t: 'activity', a } : null
    }
    case 'looks': {
      const cells = parseLookCells(msg.cells)
      return cells ? { t: 'looks', cells } : null
    }
    case 'pins': {
      const pins = parsePins(msg.pins)
      return pins ? { t: 'pins', pins } : null
    }
    case 'tally': {
      const tally = parseTally(msg.tally)
      return tally ? { t: 'tally', tally } : null
    }
    case 'results': {
      if (msg.r === null) return { t: 'results', r: null }
      const r = parseResults(msg.r)
      return r ? { t: 'results', r } : null
    }
    case 'hands': {
      const hands = parseHands(msg.hands)
      return hands ? { t: 'hands', hands } : null
    }
    case 'game': {
      if (msg.v === null) return { t: 'game', v: null }
      const v = parseGameView(msg.v)
      return v ? { t: 'game', v } : null
    }
    case 'people': {
      const people = parsePeople(msg.people)
      return people ? { t: 'people', people } : null
    }
    case 'log': {
      const acts = parseLog(msg.acts)
      return acts ? { t: 'log', acts } : null
    }
    case 'logRows': {
      const rows = parseLogRows(msg.rows)
      return isActivityId(msg.id) && rows ? { t: 'logRows', id: msg.id, rows } : null
    }
    case 'mine': {
      const mine = parseMine(msg.mine)
      return mine ? { t: 'mine', mine } : null
    }
    case 'kicked':
      return { t: 'kicked' }
    case 'ended':
      return { t: 'ended' }
    case 'stage': {
      const me = msg.me === null ? null : parseMyStage(msg.me)
      if (msg.me !== null && !me) return null
      const why = msg.why === 'removed' || msg.why === 'muted' ? msg.why : undefined
      return { t: 'stage', me, ...(why ? { why } : {}) }
    }
  }
  if (msg.t === 'state') {
    const s = parseRoomState(msg.s)
    return s ? { t: 'state', s } : null
  }
  if (msg.t === 'slot') {
    const s = parseSlotUpdate(msg.s)
    return isSlotIndex(msg.n) && s ? { t: 'slot', n: msg.n, s } : null
  }
  const revOrNull = (v: unknown): v is number | null => v === null || (finite(v) && v >= 0)
  if (msg.t === 'media') {
    return revOrNull(msg.rev)
      ? { t: 'media', rev: msg.rev, drivers: parseDriversLive(msg.drivers), spot: finite(msg.spot) && msg.spot >= 0 ? msg.spot : null, stage: parseStageLive(msg.stage) }
      : null
  }
  if (msg.t !== 'welcome' && msg.t !== 'roster') return null
  if (!idOrNull(msg.lead) || !finite(msg.count) || msg.count < 0) return null
  // Absent on a room from before meetings existed: an open room.
  const meeting = msg.meeting === true
  // Absent on a room from before the globe could be handed over: the presenter has it.
  const spotlight = meeting && msg.spotlight ? parseSpotlight(msg.spotlight) : null
  // Absent on a room from before globes could be driven: nobody drives one.
  const drivers = meeting ? parseDrivers(msg.drivers) : []
  // Absent on a room from before the stage: nobody on it.
  const stage = meeting ? parseStageMembers(msg.stage) : []
  if (msg.t === 'roster') return { t: 'roster', lead: msg.lead, count: msg.count, meeting, spotlight, drivers, stage }
  if (typeof msg.you !== 'string' || msg.you.length === 0 || msg.you.length > 64) return null
  const state = msg.state === null || msg.state === undefined ? null : parseRoomState(msg.state)
  return {
    t: 'welcome',
    you: msg.you,
    lead: msg.lead,
    count: msg.count,
    state,
    meeting,
    seat: meeting ? (parseRoomSeat(msg.seat) ?? 'audience') : null,
    // Absent on a room from before live media existed: not live.
    media: meeting && revOrNull(msg.media) ? msg.media : null,
    ticket: meeting && typeof msg.ticket === 'string' && /^[\w-]{1,64}$/.test(msg.ticket) ? msg.ticket : null,
    // Absent on a room from before activities existed: none under way.
    activity: msg.activity ? parseRoomActivity(msg.activity) : null,
    results: msg.results ? parseResults(msg.results) : null,
    spotlight,
    // Absent on a room from before lending existed: nothing lent.
    grants: meeting ? parseGrants(msg.grants) : null,
    drivers,
    driversLive: meeting ? parseDriversLive(msg.driversLive) : [],
    // Absent on a room from before the main globe's driver could speak: off.
    spotLive: meeting && finite(msg.spotLive) && msg.spotLive >= 0 ? msg.spotLive : null,
    // Absent on a room from before people were numbered: none.
    me: meeting ? parseMe(msg.me) : null,
    stage,
    stageLive: meeting ? parseStageLive(msg.stageLive) : [],
    myStage: meeting ? parseMyStage(msg.myStage) : null,
    driven: meeting && Array.isArray(msg.driven)
      ? (msg.driven as unknown[]).flatMap((item) => {
          const d = item as Record<string, unknown> | null
          const s = d && typeof d === 'object' ? parseSlotUpdate(d.s) : null
          return d && isSlotIndex(d.n) && s ? [{ n: d.n, s }] : []
        })
      : [],
  }
}

/** This person in a meeting, as the welcome says; null when it says nothing. */
function parseMe(raw: unknown): { n: number; name: string | null } | null {
  if (typeof raw !== 'object' || raw === null) return null
  const m = raw as Record<string, unknown>
  return Number.isInteger(m.n) && (m.n as number) >= 1 ? { n: m.n as number, name: cleanText(m.name, NAME_CHARS) } : null
}

/** What a meeting lends, as received; null when nothing is. */
export function parseGrants(raw: unknown): RoomGrants | null {
  if (typeof raw !== 'object' || raw === null) return null
  const g = raw as { data?: unknown; globes?: unknown; games?: unknown; stage?: unknown; packages?: unknown }
  const data = g.data === true
  const globes = Number.isInteger(g.globes) && (g.globes as number) >= 2 && (g.globes as number) <= ROOM_MAX_SLOTS ? (g.globes as number) : null
  const games = g.games === true
  const stage = g.stage === 'open' || g.stage === 'class' ? g.stage : null
  const packages = parseLentPackages(g.packages)
  if (!data && globes === null && !games && !stage && !packages.length) return null
  return { data, ...(globes !== null ? { globes } : {}), ...(games ? { games } : {}), ...(stage ? { stage } : {}), ...(packages.length ? { packages } : {}) }
}
